// semeia-disable-reexport
import type { IntlService } from "ember-intl";
import type MedicModel from "parog-web/models/medic";
import type Patient from "parog-web/models/patient";

export function possibleTypes(
  intl: IntlService,
  patient?: Patient,
  medic?: MedicModel
): string[] {
  // These lists: coreTypes, mentalTypes, and digestTypes are sorted alphabetically
  // according to their translation. Be careful when adding items to them.
  let coreTypes = [
    "bio-exams-missing",
    "call-patient-train-tls",
    "call-patient-suggest-tls",
    "bio-exams-received",
    "identify-consolidation-high-priority",
    "pps-control-biology",
    "pps-control-vital-constants",
    "identity-control",
    "unregistered-dead-patient",
    "lab-failure",
    "inclusion-finalization",
    "inactive",
    "investigate-which-hc-tls",
    "measurement-received",
    "no-data",
    "lost-sight",
    "recurring-monitoring-point",
    "collect-tls-consent",
    "measurement-record",
    "user-nc-response",
    "biology-location-check",
    "tensiometer-check"
  ];

  const mentalTypes = [
    "bio-exams-missing",
    "home-support",
    "ponctual-monitoring-point",
    "dashboard-monitoring",
    "call-treating-physician",
    "call-patient",
    "call-psychiatrist"
  ];

  const oncoTypes = [
    "planify-follow-up-first-results",
    "follow-up-first-results",
    "planify-follow-up-remission",
    "follow-up-remission",
    "planify-follow-up-monitoring",
    "follow-up-monitoring",
    "planify-pharmaceutical-interview",
    "pharmaceutical-interview",
    "visit-call",
    "administrative",
    "incoming-patient-call",
    "incoming-health-professional-call",
    "post-reconstruction-call",
    "outgoing-patient-call",
    "outgoing-health-professional-call",
    "cp-patient-appointment",
    "room-visit",
    "patient-visit",
    "internal-meeting",
    "external-meeting",
    "professional-appointment",
    "biological-assessment-ecbu",
    "biological-analysis",
    "biological-assessment-psa",
    "medical-consultation",
    "measure-vital-sign-weight",
    "measure-vital-sign-blood-pressure",
    "measure-vital-sign-temperature",
    "re-evaluation-scanner"
  ];

  const digestTypes = [
    "bio-exams-missing",
    "pps-control-treatment",
    "recurring-monitoring-point"
  ];

  const isgtTasks = [
    "pre-operating-call",
    "post-operating-call",
    "request-cs-post-operating-date",
    "post-operating-consultation-call",
    "recovery-pps-oncology",
    "visit-call",
    "chemotherapy-point",
    "pps-radiotherapy-call",
    "start-radiotherapy-call",
    "end-radiotherapy-call",
    "j-30-call",
    "j-90-call",
    "j-180-call",
    "j-365-call",
    "end-targeted-therapy-call"
  ];

  if (patient) {
    const areaId = patient.therapeuticalArea?.id;

    if (areaId == "mental") return mentalTypes;

    if (areaId == "onco") {
      let oncoTasks = coreTypes.concat(oncoTypes);

      if (patient.healthCentre.isgt) {
        oncoTasks = oncoTasks.concat(isgtTasks);
      }

      return sortedTaskTypes(oncoTasks, intl);
    }

    if (areaId == "digest") return digestTypes;

    return coreTypes;
  }

  const medicTherapeuticalAreaIds = medic?.therapeuticalAreaIds;

  if (medicTherapeuticalAreaIds?.includes("onco")) {
    coreTypes = coreTypes.concat(oncoTypes);
    if (medic?.healthCentres.some(hc => hc.isgt)) {
      coreTypes = coreTypes.concat(isgtTasks);
    }
  }
  if (medicTherapeuticalAreaIds?.includes("mental")) {
    coreTypes = coreTypes.concat(mentalTypes);
  }

  return sortedTaskTypes(coreTypes, intl);
}

function sortedTaskTypes(typesList, intl) {
  const sortedList = typesList
    .slice()
    .sort((a, b) =>
      intl.t(`task.types.${a}`).localeCompare(intl.t(`task.types.${b}`))
    );
  return sortedList;
}
